import React from 'react';

const Logo = ({ 
  variant = 'default', // 'default', 'white', 'compact', 'icon-only'
  size = 'md', // 'sm', 'md', 'lg', 'xl'
  className = '' 
}) => {
  const sizeClasses = {
    sm: 'w-32 h-8',
    md: 'w-40 h-10',
    lg: 'w-48 h-12',
    xl: 'w-60 h-15'
  };

  const iconSizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16'
  };

  if (variant === 'icon-only') {
    return (
      <div className={`${iconSizeClasses[size]} ${className}`}>
        <svg viewBox="0 0 100 100" className="w-full h-full">
          {/* Geometric mountain/peak representing "Vantage Points" */}
          <defs>
            <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#1a1a1a" />
              <stop offset="100%" stopColor="#4a4a4a" />
            </linearGradient>
            <linearGradient id="logoGradientWhite" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="100%" stopColor="#f0f0f0" />
            </linearGradient>
          </defs>
          
          {/* Main diamond/peak shape */}
          <polygon 
            points="50,15 75,40 50,65 25,40" 
            fill={variant === 'white' ? 'url(#logoGradientWhite)' : 'url(#logoGradient)'}
            className="drop-shadow-sm"
          />
          
          {/* Inner geometric detail */}
          <polygon 
            points="50,25 65,40 50,55 35,40" 
            fill={variant === 'white' ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)'}
          />
          
          {/* Elegant line detail */}
          <line 
            x1="30" y1="75" x2="70" y2="75" 
            stroke={variant === 'white' ? '#ffffff' : '#1a1a1a'} 
            strokeWidth="1.5" 
            strokeLinecap="round"
          />
          
          {/* Small accent dots */}
          <circle cx="25" cy="75" r="1.5" fill={variant === 'white' ? '#ffffff' : '#1a1a1a'} />
          <circle cx="75" cy="75" r="1.5" fill={variant === 'white' ? '#ffffff' : '#1a1a1a'} />
        </svg>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <div className="w-8 h-8">
          <svg viewBox="0 0 100 100" className="w-full h-full">
            <defs>
              <linearGradient id="compactGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#1a1a1a" />
                <stop offset="100%" stopColor="#4a4a4a" />
              </linearGradient>
            </defs>
            <polygon 
              points="50,15 75,40 50,65 25,40" 
              fill="url(#compactGradient)"
              className="drop-shadow-sm"
            />
            <polygon 
              points="50,25 65,40 50,55 35,40" 
              fill="rgba(255,255,255,0.1)"
            />
          </svg>
        </div>
        <div className="flex flex-col">
          <span className={`font-light text-lg leading-none ${
            variant === 'white' ? 'text-white' : 'text-gray-900'
          }`}>
            VP
          </span>
          <span className={`text-xs font-light leading-none tracking-wider ${
            variant === 'white' ? 'text-white/70' : 'text-gray-500'
          }`}>
            HOSPITALITY
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-center space-x-3 ${className}`}>
      {/* Icon */}
      <div className="relative">
        <svg viewBox="0 0 100 100" className="w-10 h-10">
          <defs>
            <linearGradient id="mainGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#1a1a1a" />
              <stop offset="100%" stopColor="#4a4a4a" />
            </linearGradient>
            <linearGradient id="whiteGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="100%" stopColor="#f0f0f0" />
            </linearGradient>
          </defs>
          
          {/* Main geometric shape - representing elevation/vantage points */}
          <polygon 
            points="50,15 75,40 50,65 25,40" 
            fill={variant === 'white' ? 'url(#whiteGradient)' : 'url(#mainGradient)'}
            className="drop-shadow-sm"
          />
          
          {/* Inner detail for depth */}
          <polygon 
            points="50,25 65,40 50,55 35,40" 
            fill={variant === 'white' ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)'}
          />
          
          {/* Elegant baseline */}
          <line 
            x1="30" y1="75" x2="70" y2="75" 
            stroke={variant === 'white' ? '#ffffff' : '#1a1a1a'} 
            strokeWidth="1.5" 
            strokeLinecap="round"
          />
          
          {/* Refined accent points */}
          <circle cx="25" cy="75" r="1.5" fill={variant === 'white' ? '#ffffff' : '#1a1a1a'} />
          <circle cx="75" cy="75" r="1.5" fill={variant === 'white' ? '#ffffff' : '#1a1a1a'} />
        </svg>
      </div>
      
      {/* Wordmark */}
      <div className="flex flex-col">
        <span className={`font-light text-xl leading-none tracking-tight ${
          variant === 'white' ? 'text-white' : 'text-gray-900'
        }`}>
          Vantage Points
        </span>
        <span className={`text-xs font-light leading-none tracking-widest mt-0.5 ${
          variant === 'white' ? 'text-white/70' : 'text-gray-500'
        }`}>
          HOSPITALITY
        </span>
      </div>
    </div>
  );
};

export default Logo;